/**
 * @module game/physics
 * Pure parabolic physics update functions for fruit, bomb, and power-up entities.
 *
 * All functions are free of side-effects and operate on plain value objects,
 * making them straightforward to unit-test without DOM or game-loop setup.
 *
 * Integration scheme: semi-implicit Euler (velocity updated first, then
 * position). Simple and stable for the short flight times involved.
 */

/** Minimal physics body — position + velocity. */
export interface PhysicsBody {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/**
 * Advances one physics body by `dtSec` seconds under constant downward gravity.
 * Mutates and returns the same object so callers can chain if desired.
 *
 * @param body - Entity to advance (mutated in place).
 * @param dtSec - Frame delta in seconds.
 * @param gravity - Downward acceleration (px/s²) — positive = downward.
 * @param velocityScale - Multiplier on velocity this frame (1 = normal, 0.2 = freeze).
 */
export function stepBody(
  body: PhysicsBody,
  dtSec: number,
  gravity: number,
  velocityScale = 1,
): PhysicsBody {
  // Semi-implicit Euler: velocity first, then position.
  body.vy += gravity * dtSec;
  body.x += body.vx * velocityScale * dtSec;
  body.y += body.vy * velocityScale * dtSec;
  return body;
}

/**
 * Returns `true` when the entity has fallen below the bottom of the canvas
 * and will not return (i.e. downward velocity and past bottom edge).
 *
 * @param body - Entity to test.
 * @param canvasHeight - Height of the game canvas in pixels.
 * @param margin - Extra pixels below the edge before marking offscreen (default 80).
 */
export function isOffscreen(
  body: PhysicsBody,
  canvasHeight: number,
  margin = 80,
): boolean {
  return body.y > canvasHeight + margin && body.vy > 0;
}

/**
 * Computes the launch velocity components for a projectile aimed to reach
 * a peak height and land at a target X position.
 *
 * Used by the Spawner to produce varied, visually appealing arcs.
 *
 * @param speed - Launch speed magnitude (px/s) from CONFIG.fruit.baseSpeed.
 * @param angleDeg - Launch angle in degrees measured from horizontal (90 = straight up).
 * @param mirrorX - If true, launch toward the left (negative vx).
 */
export function computeLaunchVelocity(
  speed: number,
  angleDeg: number,
  mirrorX = false,
): { vx: number; vy: number } {
  const rad = (angleDeg * Math.PI) / 180;
  const vx = Math.cos(rad) * speed * (mirrorX ? -1 : 1);
  const vy = -Math.abs(Math.sin(rad) * speed); // always upward
  return { vx, vy };
}
